import { useEffect, useState, type ReactNode } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import {
  ArrowLeft,
  Ban,
  Cable,
  Check,
  Copy,
  Cpu,
  Eye,
  EyeOff,
  ExternalLink,
  FileCode,
  Globe,
  Info,
  Key,
  Layers,
  Lock,
  MapPin,
  Power,
  RefreshCw,
  Router,
  ShieldCheck,
  Signal,
  Trash2,
  Tv,
  Wifi,
  WifiOff,
  Zap,
} from "lucide-react"
import {
  smartoltService,
  type SmartOLTAlarmRow,
  type SmartOLTOltMgmtIpRow,
  type SmartOLTOnuRow,
  type SmartOLTTopology,
} from "../../../services/smartolt.service.ts"
import { LiveTrafficTestConsole } from "../../../components/smartolt/LiveTrafficTestConsole.tsx"
import {
  SmartOLTActionModals,
  type ModalContextData,
  type ModalType,
} from "../../../components/smartolt/SmartOLTActionModals.tsx"
import { openOnuWebAdminLink } from "../../../utils/openOnuWebAdmin.ts"
import { toast } from "../../../utils/notify.ts"
import { SMARTOLT_PATHS } from "../smartoltNav.ts"
import { dateTime } from "../utils.ts"
import { formatAuthDate, formatSignal, statusKind, wanLabel } from "./smartoltConfiguredUtils.ts"
import { queueSmartOltOnuCommand, unwrapLiveOnu } from "./smartoltOnuCommands.ts"
import { SmartOLTMgmtIpModal } from "./SmartOLTMgmtIpModal.tsx"
import { extractPresetList } from "./smartoltOltDetailUtils.ts"

type Tab = "overview" | "stats" | "graphs" | "configuration" | "actions" | "alarms" | "crm"
type GraphKind = "signal" | "traffic"
type GraphType = "hourly" | "daily" | "weekly" | "monthly" | "yearly"
type GraphSubTab = "smartolt_graphs" | "live_test"
type AlarmsSubTab = "alarms" | "commands"
type ActionCategory = "quick" | "wan" | "lan" | "security" | "services"
type DiagnosticKind = "status" | "config" | "software" | "router_hosts" | "speed_profiles" | "details"

function asList(value: unknown): any[] {
  return Array.isArray(value) ? value : []
}

function dash(value: unknown) {
  if (value == null || value === "" || value === "-") return "-"
  return String(value)
}

function firstValue(...values: unknown[]) {
  return values.find((value) => value != null && value !== "" && value !== "-")
}

function readAny(source: any, ...keys: string[]) {
  if (!source || typeof source !== "object") return undefined
  for (const key of keys) {
    const value = source[key]
    if (value != null && value !== "" && value !== "-") return value
  }
  return undefined
}

function formatMeters(value: unknown) {
  const raw = firstValue(value)
  if (raw == null) return "-"
  return `${raw} m`
}

function formatMetric(value: unknown, suffix: string) {
  const raw = firstValue(value)
  if (raw == null) return "-"
  return `${raw}${String(raw).includes(suffix) ? "" : ` ${suffix}`}`
}

function configuredHref(params: Record<string, string | undefined>) {
  const query = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value) query.set(key, value)
  })
  const text = query.toString()
  return text ? `${SMARTOLT_PATHS.configured}?${text}` : SMARTOLT_PATHS.configured
}

function prettyPayload(payload: unknown) {
  if (payload == null) return ""
  if (typeof payload === "string") return payload
  try {
    return JSON.stringify(payload, null, 2)
  } catch {
    return String(payload)
  }
}

type RouterHostRow = {
  macAddress?: string
  ipAddress?: string
  addressSource?: string
  hostname?: string
  port?: string
  active?: unknown
}

function extractRouterHosts(payload: any): RouterHostRow[] {
  const source = payload?.hostlist || payload?.response?.hostlist || payload?.data?.hostlist || payload?.router_hosts || payload?.response || payload?.data || payload
  const list = Array.isArray(source) ? source : source && typeof source === "object" ? Object.values(source) : []
  return list.filter((item: any) => item && typeof item === "object").map((item: any) => ({
    macAddress: item.MACAddress || item.mac_address || item.mac,
    ipAddress: item.IPAddress || item.ip_address || item.ip,
    addressSource: item.AddressSource || item.address_source,
    hostname: item.HostName || item.hostname || item.name,
    port: item.InterfaceType || item.Layer2Interface || item.port,
    active: item.Active ?? item.active,
  }))
}

function activeText(value: unknown) {
  if (typeof value === "boolean") return value ? "Yes" : "No"
  const text = String(value ?? "").trim()
  if (!text) return "-"
  return ["1", "true", "yes", "active", "enabled"].includes(text.toLowerCase()) ? "Yes" : text
}

function RouterHostsTable({ hosts }: { hosts: RouterHostRow[] }) {
  return (
    <div className="max-h-96 overflow-auto rounded-xl border border-slate-200 bg-white">
      <table className="w-full min-w-[760px] text-xs">
        <thead className="sticky top-0 bg-slate-50 text-left uppercase tracking-wider text-slate-500">
          <tr>
            <th className="px-3 py-2">#</th>
            <th className="px-3 py-2">MAC Address</th>
            <th className="px-3 py-2">IP Address</th>
            <th className="px-3 py-2">Address source</th>
            <th className="px-3 py-2">Hostname</th>
            <th className="px-3 py-2">Port</th>
            <th className="px-3 py-2">Active</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {hosts.length === 0 ? (
            <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-500">No router hosts reported.</td></tr>
          ) : hosts.map((host, index) => (
            <tr key={`${host.macAddress || host.ipAddress || index}`} className="hover:bg-slate-50">
              <td className="px-3 py-2 font-mono text-slate-500">{index + 1}</td>
              <td className="px-3 py-2 font-mono text-slate-800">{host.macAddress || "-"}</td>
              <td className="px-3 py-2 font-mono text-slate-800">{host.ipAddress || "-"}</td>
              <td className="px-3 py-2 text-slate-700">{host.addressSource || "-"}</td>
              <td className="px-3 py-2 font-semibold text-slate-900">{host.hostname || "-"}</td>
              <td className="px-3 py-2 text-slate-700">{host.port || "-"}</td>
              <td className="px-3 py-2"><span className="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-semibold">{activeText(host.active)}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function extractRunningConfig(payload: any) {
  return (
    payload?.running_config ||
    payload?.response?.running_config ||
    payload?.data?.running_config ||
    prettyPayload(payload)
  )
}

function extractSoftware(payload: any, live: any, row: SmartOLTOnuRow | null) {
  const source = payload?.onu_details || payload?.response || payload?.data || payload || {}
  return [
    ["Software version", source.software_version || live.software_version || row?.onu_type_name],
    ["Hardware version", source.hardware_version || live.hardware_version],
    ["ONU type", source.onu_type_name || live.onu_type_name || row?.onu_type_name],
    ["SN", source.sn || live.sn || row?.sn],
    ["Firmware", source.firmware || live.firmware],
  ].filter(([, value]) => value != null && value !== "")
}

function objectValues(source: any) {
  if (!source || typeof source !== "object") return []
  return Array.isArray(source) ? source : Object.values(source)
}

function sectionObject(payload: any, name: string) {
  return payload?.full_status_json?.[name] || payload?.[name] || null
}

function normalizedPayload(payload: any) {
  return payload?.response || payload?.data || payload || {}
}

function FieldGrid({ rows }: { rows: Array<[string, unknown]> }) {
  const cleanRows = rows.filter(([, value]) => value != null && value !== "")
  if (cleanRows.length === 0) return null
  return (
    <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
      {cleanRows.map(([label, value]) => (
        <div key={label} className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-xs">
          <dt className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{label}</dt>
          <dd className="mt-1 break-words text-sm font-semibold text-slate-900">{String(value)}</dd>
        </div>
      ))}
    </div>
  )
}

function DataTable({ columns, rows, empty }: { columns: string[]; rows: any[]; empty: string }) {
  return (
    <div className="overflow-auto rounded-xl border border-slate-200 bg-white shadow-xs">
      <table className="w-full min-w-[680px] text-xs">
        <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500">
          <tr>{columns.map((column) => <th key={column} className="px-3 py-2">{column}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-slate-500">{empty}</td></tr>
          ) : rows.map((row, index) => (
            <tr key={row.id || row.port || row.Name || row["MAC address"] || index} className="hover:bg-slate-50">
              {columns.map((column) => (
                <td key={column} className="px-3 py-2 text-slate-700">
                  <span className={column.toLowerCase().includes("mac") || column.toLowerCase().includes("ip") || column === "#" ? "font-mono text-slate-900" : ""}>
                    {String(row[column] ?? row[column.replace(/\s+/g, "_").toLowerCase()] ?? "-")}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function DiagnosticShell({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50/40 p-3 shadow-xs">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-sm font-bold text-slate-900">{title}</h3>
          {subtitle ? <p className="mt-0.5 text-xs text-slate-500">{subtitle}</p> : null}
        </div>
      </div>
      {children}
    </section>
  )
}

function RunningConfigViewer({ payload }: { payload: any }) {
  const config = String(extractRunningConfig(payload) || "")
  const ontMatch = config.match(/ont add\s+(\d+)\s+(\d+)\s+sn-auth\s+"?([^"\s]+)"?.*?ont-lineprofile-id\s+(\d+).*?ont-srvprofile-id\s+(\d+)/i)
  const ipMatch = config.match(/static ip-address\s+([^\s]+)\s+mask\s+([^\s]+)\s+vlan\s+([^\s]+).*?gateway\s+([^\s]+).*?pri-dns\s+([^\s]+)\s+slave-dns\s+([^\s]+)/i)
  const servicePorts = [...config.matchAll(/service-port\s+(\d+)\s+vlan\s+(\d+).*?gemport\s+(\d+).*?user-vlan\s+(\d+).*?inbound traffic-table index\s+(\d+)\s+outbound traffic-table index\s+(\d+)/gi)]
    .map((match) => ({
      "#": match[1],
      VLAN: match[2],
      GEM: match[3],
      "User VLAN": match[4],
      Inbound: match[5],
      Outbound: match[6],
    }))

  return (
    <DiagnosticShell title="Running configuration" subtitle="Parsed SmartOLT provisioning lines with the raw command retained for audit.">
      <div className="space-y-3">
        <FieldGrid rows={[
          ["Board", ontMatch?.[1]],
          ["ONU", ontMatch?.[2]],
          ["Serial", ontMatch?.[3]],
          ["Line profile", ontMatch?.[4]],
          ["Service profile", ontMatch?.[5]],
          ["Management IP", ipMatch?.[1]],
          ["Subnet mask", ipMatch?.[2]],
          ["Management VLAN", ipMatch?.[3]],
          ["Gateway", ipMatch?.[4]],
          ["DNS", ipMatch ? `${ipMatch[5]} / ${ipMatch[6]}` : null],
        ]} />
        <DataTable columns={["#", "VLAN", "GEM", "User VLAN", "Inbound", "Outbound"]} rows={servicePorts} empty="No service-port entries found in running config." />
        <pre className="max-h-56 overflow-auto rounded-xl border border-slate-200 bg-slate-950 p-3 font-mono text-[11px] leading-relaxed text-slate-100">{config || "No running config returned."}</pre>
      </div>
    </DiagnosticShell>
  )
}

function StatusViewer({ payload }: { payload: any }) {
  const data = normalizedPayload(payload)
  const optical = sectionObject(data, "Optical status") || {}
  const onuDetails = sectionObject(data, "ONU details") || {}
  const history = objectValues(sectionObject(data, "History"))
  const wan = objectValues(sectionObject(data, "ONU WAN Interfaces"))
  const lan = objectValues(sectionObject(data, "ONU LAN Interfaces status")).map((row: any, index) => ({ Port: index + 1, ...row }))
  const oltMacs = objectValues(sectionObject(data, "MACs on OLT from this ONU"))
  const onuMacs = objectValues(sectionObject(data, "Online MACs on this ONU"))
  return (
    <DiagnosticShell title="ONU status" subtitle="Optical health, uptime history, WAN interfaces, and learned MACs.">
      <div className="space-y-4">
        <FieldGrid rows={[
          ["Module", optical["Module type"]],
          ["ONU RX", optical["Rx optical power(dBm)"] ? `${optical["Rx optical power(dBm)"]} dBm` : null],
          ["ONU TX", optical["Tx optical power(dBm)"] ? `${optical["Tx optical power(dBm)"]} dBm` : null],
          ["OLT RX", optical["OLT Rx ONT optical power(dBm)"] ? `${optical["OLT Rx ONT optical power(dBm)"]} dBm` : null],
          ["Optical temp", optical["Temperature(C)"] ? `${optical["Temperature(C)"]} C` : null],
          ["Run state", onuDetails["Run state"]],
          ["Match state", onuDetails["Match state"]],
          ["Distance", onuDetails["ONT distance(m)"] ? `${onuDetails["ONT distance(m)"]} m` : null],
          ["CPU", onuDetails["CPU occupation"]],
          ["Memory", onuDetails["Memory occupation"]],
          ["Last down cause", onuDetails["Last down cause"]],
          ["Online duration", onuDetails["ONT online duration"]],
        ]} />
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">WAN interfaces</h4>
          <DataTable columns={["Name", "Service type", "IPv4 Connection status", "IPv4 access type", "IPv4 address", "Manage VLAN", "Default gateway"]} rows={wan} empty="No WAN interfaces reported." />
        </div>
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">LAN ports</h4>
          <DataTable columns={["Port", "Type", "Speed", "Duplex", "Link state", "Ring Status"]} rows={lan} empty="No LAN interfaces reported." />
        </div>
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">History</h4>
          <DataTable columns={["Auth at", "Offline at", "Cause"]} rows={history} empty="No status history reported." />
        </div>
        <div className="grid gap-3 lg:grid-cols-2">
          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">OLT MAC table</h4>
            <DataTable columns={["Service port", "MAC address", "Type", "Vlan"]} rows={oltMacs} empty="No OLT MACs reported." />
          </div>
          <div>
            <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Online ONU MACs</h4>
            <DataTable columns={["Port", "MAC address", "VLAN"]} rows={onuMacs} empty="No online ONU MACs reported." />
          </div>
        </div>
      </div>
    </DiagnosticShell>
  )
}

function SoftwareViewer({ payload, live, row }: { payload: any; live: any; row: SmartOLTOnuRow | null }) {
  const rows = extractSoftware(payload, live, row)
  return (
    <DiagnosticShell title="Software and hardware" subtitle="Device identity returned by SmartOLT.">
      <FieldGrid rows={rows as Array<[string, unknown]>} />
    </DiagnosticShell>
  )
}

function SpeedProfilesViewer({ payload }: { payload: any }) {
  const data = normalizedPayload(payload)
  const success = data.status === true || String(data.response_code || "").toLowerCase() === "success"
  return (
    <DiagnosticShell title="Speed profiles" subtitle="Current downstream and upstream service profile assignment.">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-blue-700">Download</p>
          <p className="mt-2 text-2xl font-black text-blue-900">{data.download_speed_profile_name || "-"}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
          <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Upload</p>
          <p className="mt-2 text-2xl font-black text-emerald-900">{data.upload_speed_profile_name || "-"}</p>
        </div>
        <div className={`rounded-xl border p-4 ${success ? "border-emerald-100 bg-emerald-50" : "border-rose-100 bg-rose-50"}`}>
          <p className={`text-xs font-bold uppercase tracking-wide ${success ? "text-emerald-700" : "text-rose-700"}`}>Response</p>
          <p className={`mt-2 text-lg font-black ${success ? "text-emerald-900" : "text-rose-900"}`}>{data.response_code || (success ? "success" : "unknown")}</p>
        </div>
      </div>
    </DiagnosticShell>
  )
}

function FullDetailsViewer({ payload }: { payload: any }) {
  const data = normalizedPayload(payload)
  const detail = data.onu_details || data
  const ethernet = objectValues(detail.ethernet_ports)
  const wifi = objectValues(detail.wifi_ports)
  const servicePorts = objectValues(detail.service_ports)
  return (
    <DiagnosticShell title="Full ONU details" subtitle="Structured inventory, interfaces, and service bindings.">
      <div className="space-y-4">
        <FieldGrid rows={[
          ["Address", detail.address],
          ["Admin status", detail.administrative_status],
          ["Authorization date", detail.authorization_date],
          ["Board", detail.board],
          ["Port", detail.port],
          ["ONU", detail.onu],
          ["Distance", detail.distance ? `${detail.distance} m` : null],
          ["Contact", detail.contact],
          ["Gateway", detail.default_gateway],
          ["DNS", [detail.dns1, detail.dns2].filter(Boolean).join(" / ")],
        ]} />
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Ethernet ports</h4>
          <DataTable columns={["port", "admin_state", "dhcp", "allowed_vlans", "mode"]} rows={ethernet} empty="No ethernet ports reported." />
        </div>
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Wi-Fi ports</h4>
          <DataTable columns={["port", "ssid", "admin_state", "auth_mode", "encryption_mode"]} rows={wifi} empty="No Wi-Fi ports reported." />
        </div>
        <div>
          <h4 className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-500">Service ports</h4>
          <DataTable columns={["service_port", "vlan", "gemport", "user_vlan", "traffic_table_in", "traffic_table_out"]} rows={servicePorts} empty="No service ports reported." />
        </div>
      </div>
    </DiagnosticShell>
  )
}

function LiveSignalViewer({ payload, error }: { payload: any; error?: string }) {
  const data = normalizedPayload(payload)
  if (error) {
    return <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm font-semibold text-rose-700">{error}</div>
  }
  return (
    <DiagnosticShell title="Live optical monitor" subtitle="Auto-refreshing every 8 seconds.">
      <FieldGrid rows={[
        ["Status", data.status === true ? "Online read successful" : data.status],
        ["Signal health", data.onu_signal],
        ["ONU / OLT value", data.onu_signal_value],
        ["1310 nm", data.onu_signal_1310 ? `${data.onu_signal_1310} dBm` : null],
        ["1490 nm", data.onu_signal_1490 ? `${data.onu_signal_1490} dBm` : null],
        ["Response", data.response_code],
      ]} />
    </DiagnosticShell>
  )
}

function DiagnosticResultViewer({ kind, payload, live, row }: { kind: DiagnosticKind | null; payload: any; live: any; row: SmartOLTOnuRow | null }) {
  if (!kind || !payload) return null
  if (kind === "status") return <StatusViewer payload={payload} />
  if (kind === "config") return <RunningConfigViewer payload={payload} />
  if (kind === "software") return <SoftwareViewer payload={payload} live={live} row={row} />
  if (kind === "speed_profiles") return <SpeedProfilesViewer payload={payload} />
  if (kind === "details") return <FullDetailsViewer payload={payload} />
  return null
}

export function SmartOLTOnuDetailPage() {
  const { onuId = "" } = useParams()
  const navigate = useNavigate()
  const [row, setRow] = useState<SmartOLTOnuRow | null>(null)
  const [details, setDetails] = useState<any>(null)
  const [topology, setTopology] = useState<SmartOLTTopology | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [tab, setTab] = useState<Tab>("overview")
  const [graphKind, setGraphKind] = useState<GraphKind>("signal")
  const [graphType, setGraphType] = useState<GraphType>("hourly")
  const [graphUrl, setGraphUrl] = useState<string | null>(null)
  const [graphLoading, setGraphLoading] = useState(false)
  const [commands, setCommands] = useState<any[]>([])
  const [diagnosticKind, setDiagnosticKind] = useState<DiagnosticKind | null>(null)
  const [diagnosticPayload, setDiagnosticPayload] = useState<any>(null)
  const [diagnosticText, setDiagnosticText] = useState("")
  const [routerHosts, setRouterHosts] = useState<RouterHostRow[] | null>(null)
  const [diagnosticBusy, setDiagnosticBusy] = useState(false)
  const [liveMonitor, setLiveMonitor] = useState(false)
  const [liveSample, setLiveSample] = useState<any>(null)
  const [liveError, setLiveError] = useState("")
  const [activeModal, setActiveModal] = useState<ModalType>(null)
  const [modalContext, setModalContext] = useState<ModalContextData | null>(null)
  const [voipPort, setVoipPort] = useState<any | null>(null)
  const [voipForm, setVoipForm] = useState({ voip_profile_name: "", phone_number: "", sip_userid: "", password: "" })
  const [detailWarning, setDetailWarning] = useState<string | null>(null)
  const [actionCategory, setActionCategory] = useState<ActionCategory>("quick")
  const [graphSubTab, setGraphSubTab] = useState<GraphSubTab>("smartolt_graphs")
  const [alarmsSubTab, setAlarmsSubTab] = useState<AlarmsSubTab>("alarms")
  const [alarms, setAlarms] = useState<SmartOLTAlarmRow[]>([])
  const [alarmsLoading, setAlarmsLoading] = useState(false)
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const [revealedWifi, setRevealedWifi] = useState<Record<string, boolean>>({})
  const [mgmtModalOnu, setMgmtModalOnu] = useState<SmartOLTOltMgmtIpRow | null>(null)
  const [configPresets, setConfigPresets] = useState<any[]>([])
  const [presetKey, setPresetKey] = useState("")
  const [presetBusy, setPresetBusy] = useState(false)
  const [webAdminBusy, setWebAdminBusy] = useState(false)

  const load = async (force = false) => {
    if (!onuId) return
    setLoading(true)
    setDetailWarning(null)
    try {
      const [onuRowsResult, liveResult, topoResult] = await Promise.allSettled([
        smartoltService.getOnus({ q: onuId, limit: 20 }),
        smartoltService.getOnuLiveDetail(onuId, { force, cooldown_seconds: 60 }),
        topology ? Promise.resolve(null) : smartoltService.getTopology().catch(() => null),
      ])
      const onuRows = onuRowsResult.status === "fulfilled" ? onuRowsResult.value : []
      const match =
        (Array.isArray(onuRows) ? onuRows : []).find((item) => item.onu_external_id === onuId) ||
        (Array.isArray(onuRows) ? onuRows[0] : null)
      setRow(match || null)
      if (liveResult.status === "fulfilled") {
        setDetails(liveResult.value?.data || null)
        if (liveResult.value?.upstream_error) {
          setDetailWarning(String(liveResult.value.upstream_error))
        }
      } else {
        setDetails(null)
        setDetailWarning(liveResult.reason?.response?.data?.error || liveResult.reason?.message || "Live SmartOLT detail unavailable")
      }
      if (topoResult.status === "fulfilled" && topoResult.value) setTopology(topoResult.value)
      if (onuRowsResult.status === "rejected" && liveResult.status === "rejected") {
        toast.error("Failed to load ONU details")
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to load ONU details")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [onuId])

  useEffect(() => {
    if (tab !== "graphs" || !onuId) return
    let cancelled = false
    let activeUrl: string | null = null
    const run = async () => {
      setGraphLoading(true)
      let objectUrl: string | null = null
      try {
        const blob = await smartoltService.getXrtgGraphImage(onuId, { kind: graphKind, range: graphType })
        objectUrl = URL.createObjectURL(blob)
        if (!cancelled) {
          setGraphUrl(objectUrl)
          activeUrl = objectUrl
          objectUrl = null
        }
      } catch {
        if (!cancelled) setGraphUrl(null)
      } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl)
        if (!cancelled) setGraphLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
      if (activeUrl) URL.revokeObjectURL(activeUrl)
    }
  }, [tab, onuId, graphKind, graphType])

  useEffect(() => {
    if (tab !== "alarms") return
    if (alarmsSubTab === "commands") {
      smartoltService
        .getCommands(50)
        .then((rows) => setCommands(Array.isArray(rows) ? rows.filter((c) => c.target_id === onuId) : []))
        .catch(() => setCommands([]))
      return
    }
    setAlarmsLoading(true)
    smartoltService
      .getAlarms({ q: onuId, range: "30d" })
      .then((data) => {
        const rows = Array.isArray(data?.alarms) ? data.alarms : []
        setAlarms(rows.filter((row) => String(row.onu_external_id || "") === onuId))
      })
      .catch(() => setAlarms([]))
      .finally(() => setAlarmsLoading(false))
  }, [tab, onuId, alarmsSubTab])

  useEffect(() => {
    if (tab !== "configuration" || configPresets.length > 0) return
    smartoltService
      .getConfigurationPresets()
      .then((payload) => setConfigPresets(extractPresetList(payload)))
      .catch(() => setConfigPresets([]))
  }, [tab, configPresets.length])

  useEffect(() => {
    if (tab !== "actions" && tab !== "alarms") return
    smartoltService
      .getCommands(50)
      .then((rows) => setCommands(Array.isArray(rows) ? rows.filter((c) => c.target_id === onuId) : []))
      .catch(() => setCommands([]))
  }, [tab, onuId])

  useEffect(() => {
    if (!liveMonitor || !onuId) return
    let cancelled = false
    const tick = async () => {
      try {
        const result = await smartoltService.readOperation("get_onu_signal_by_external_id", {
          path_params: { onu_external_id: onuId },
        })
        const payload = result?.response || result?.data || result
        if (!cancelled) {
          setLiveSample(payload)
          setLiveError("")
        }
      } catch (err: any) {
        if (!cancelled) {
          setLiveSample(null)
          setLiveError(err?.response?.data?.error || "Live signal read failed")
        }
      }
    }
    tick()
    const id = setInterval(tick, 8000)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [liveMonitor, onuId])

  const live = unwrapLiveOnu(details)
  const name = live.name || row?.smartolt_name || onuId
  const kind = row ? statusKind(row) : "offline"
  const servicePorts = asList(live.service_ports)
  const ethernetPorts = asList(live.ethernet_ports)
  const voipPorts = asList(live.voip_ports)
  const allWifiPorts = asList(live.wifi_ports)
  // A port is "configured" if backend explicitly says so, OR it has an SSID, OR it is enabled.
  // This handles both freshly fetched data (has 'configured' flag) and cached older payloads.
  const isWifiConfigured = (wp: any) =>
    wp.configured === true ||
    String(wp.admin_state || "").toLowerCase() === "enabled" ||
    String(wp.ssid || "").trim().length > 0
  const wifiPorts = allWifiPorts.filter(isWifiConfigured)
  const unconfiguredWifiSlots = allWifiPorts.filter((wp: any) => !isWifiConfigured(wp))
  const catvOn = String(live.catv || row?.catv_status || "").toLowerCase().includes("enable")
  const rxPower = firstValue(readAny(live, "signal", "signal_1490", "rx_power", "onu_rx_signal"), row?.signal_rx)
  const oltRxPower = firstValue(readAny(live, "signal_1310", "olt_rx_signal", "tx_power"), row?.signal_tx)
  const distance = firstValue(readAny(live, "distance", "distance_meters", "optical_distance"), row?.distance)
  const mgmtIp = firstValue(readAny(live, "mgmt_ip_address", "ip_address", "management_ip", "mgmt_ip"), row?.mgmt_ip)
  const mgmtVlan = firstValue(readAny(live, "mgmt_ip_vlan", "mgmt_vlan", "m_vlan", "management_vlan", "mvlan"), row?.mgmt_vlan)
  const mgmtGateway = firstValue(readAny(live, "mgmt_ip_default_gateway", "mgmt_gateway", "default_gateway"), row?.mgmt_gateway)
  const mgmtSubnet = firstValue(readAny(live, "mgmt_ip_subnet_mask", "mgmt_subnet", "subnet_mask"), row?.mgmt_subnet)
  const mgmtDns1 = firstValue(readAny(live, "mgmt_ip_dns1", "mgmt_dns1", "dns1", "primary_dns"))
  const mgmtDns2 = firstValue(readAny(live, "mgmt_ip_dns2", "mgmt_dns2", "dns2", "secondary_dns"))
  const pppoeUsername = firstValue(readAny(live, "pppoe_username", "pppoe_user", "username", "pppoe_login"), row?.pppoe_username)
  const pppoePassword = readAny(live, "pppoe_password", "pppoe_pass", "password")
  const vlan = firstValue(readAny(live, "vlan", "vlans", "attached_vlans"), row?.vlan)
  const wanMode = firstValue(readAny(live, "mode", "wan_mode", "onu_mode"), row?.wan_mode)
  const wanSetupMode = readAny(live, "wan_setup_mode", "wan_configuration_method", "wan_ip_mode", "wan_ip_mode_text")
  const configurationPreset = readAny(live, "configuration_preset", "configuration_preset_name", "config_preset")
  const gponChannel = readAny(live, "gpon_channel", "pon_type")
  const sector = readAny(live, "sector")
  const contact = readAny(live, "contact", "phone", "phone_number", "mobile")
  const uniqueExternalId = readAny(live, "unique_external_id", "onu_external_id") || onuId
  const temp = readAny(live, "temperature", "temp", "onu_temperature", "env_temp")
  const voltage = readAny(live, "voltage", "supply_voltage", "onu_voltage")
  const biasCurrent = readAny(live, "bias_current", "bias", "current")
  const latency = readAny(live, "latency", "latency_ms", "ping_ms")
  const downloadRate = readAny(live, "download", "download_speed", "download_mbps", "traffic_down", "down_rate")
  const uploadRate = readAny(live, "upload", "upload_speed", "upload_mbps", "traffic_up", "up_rate")
  const tr069 = firstValue(readAny(live, "tr069", "tr_069", "tr069_profile"), row?.tr069)
  const ponType = String(live.pon_type || row?.pon_type || "gpon").toLowerCase()
  const onuIf =
    live.onu ||
    `${ponType}-onu_0/${dash(live.board || row?.board)}/${dash(live.port || row?.port)}:${dash(live.onu_number || row?.onu_number)}`
  const oltId = String(live.olt_id || row?.olt_id || "")
  const board = String(live.board || row?.board || "")
  const port = String(live.port || row?.port || "")

  const identity = [
      {
        label: "OLT",
        value: live.olt_name || row?.olt_name || row?.olt_id,
        href: configuredHref({ olt: oltId }),
      },
      {
        label: "Board",
        value: live.board || row?.board,
        href: configuredHref({ olt: oltId, board }),
      },
      {
        label: "Port",
        value: live.port || row?.port,
        href: configuredHref({ olt: oltId, board, port }),
      },
      { label: "ONU", value: onuIf },
      { label: "GPON channel", value: gponChannel || ponType.toUpperCase() },
      { label: "SN", value: live.sn || row?.sn },
      {
        label: "ONU Type",
        value: live.onu_type_name || row?.onu_type_name,
        href: configuredHref({ type: String(live.onu_type_name || row?.onu_type_name || "") }),
      },
      {
        label: "Configuration Preset",
        value: configurationPreset || "None",
        action: () => setTab("configuration"),
      },
      {
        label: "Zone",
        value: live.zone_name || row?.zone_name || "None",
        href: configuredHref({ zone: String(live.zone_name || row?.zone_name || "") }),
      },
      {
        label: "ODB (Splitter)",
        value: live.odb_name || row?.odb_name,
        href: configuredHref({ odb: String(live.odb_name || row?.odb_name || "") }),
      },
      { label: "Sector", value: sector || "None" },
      { label: "Name", value: name, action: () => openModal("location") },
      { label: "Address or comment", value: live.address_or_comment || live.address || row?.address || "None" },
      { label: "Contact", value: contact || "None" },
      { label: "Authorization date", value: formatAuthDate(live.authorized_on || live.auth_date || row?.auth_date) },
      { label: "ONU external ID", value: uniqueExternalId },
      { label: "Status", value: live.status || row?.status },
      {
        label: "ONU/OLT Rx signal",
        value: `${formatSignal(rxPower)} / ${formatSignal(oltRxPower)}${
          distance ? ` (${distance}m)` : ""
        }`,
      },
      { label: "Attached VLANs", value: vlan },
      {
        label: "ONU mode",
        value: `${wanLabel(wanMode) || "-"}${
          vlan ? ` - WAN vlan:${vlan}` : ""
        }`,
      },
      {
        label: "Mgmt IP",
        value: mgmtIp
          ? `${live.mgmt_ip_mode || "Static"}${mgmtVlan ? ` - vlan:${mgmtVlan}` : ""} - ${mgmtIp}`
          : "-",
        action: () => openMgmtIpModal(),
      },
      { label: "WAN setup mode", value: wanSetupMode || "Setup via ONU webpage", action: () => openModal("wan") },
      { label: "Software version", value: live.software_version },
  ]

  const handleCopy = async (key: string, value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopiedKey(key)
      toast.success(`${label} copied`)
      setTimeout(() => setCopiedKey(null), 2000)
    } catch {
      toast.error("Copy failed")
    }
  }

  const runAction = async (operationKey: string, label: string, extra?: Record<string, unknown>) => {
    if (!onuId) return
    setBusy(true)
    try {
      await queueSmartOltOnuCommand(onuId, operationKey, extra)
      toast.success(`${label} queued`)
      if (operationKey.includes("delete")) {
        navigate(SMARTOLT_PATHS.configured)
        return
      }
      await load(true)
    } catch (err: any) {
      toast.error(err?.response?.data?.error || `Failed to ${label.toLowerCase()}`)
    } finally {
      setBusy(false)
    }
  }

  const toggleWifiRadio = async (port: string, currentAdminState?: string | null) => {
    const isEnabled = (currentAdminState || "").toLowerCase() === "enabled"
    const opKey = isEnabled ? "shutdown_onu_wifi_port_by_external_id" : "set_onu_wifi_port_mode_lan_by_external_id"
    const payload = isEnabled
      ? { form_data: { wifi_port: port } }
      : { form_data: { wifi_port: port, dhcp: "enabled" } }
    await handleModalSubmit(opKey, payload)
  }

  const toggleCatv = async () => {
    await runAction(
      catvOn ? "disable_onu_catv_by_external_id" : "enable_onu_catv_by_external_id",
      catvOn ? "Disable CATV" : "Enable CATV",
    )
  }

  const openMgmtIpModal = () => {
    setMgmtModalOnu({
      onu_external_id: onuId,
      name,
      ip_address: mgmtIp ? String(mgmtIp) : "",
      subnet_mask: mgmtSubnet ? String(mgmtSubnet) : "",
      default_gateway: mgmtGateway ? String(mgmtGateway) : "",
      dns1: mgmtDns1 ? String(mgmtDns1) : "",
      dns2: mgmtDns2 ? String(mgmtDns2) : "",
      mgmt_vlan: mgmtVlan != null ? String(mgmtVlan) : null,
      wan_mode: wanMode ? String(wanMode) : null,
      mgmt_ip_mode: live.mgmt_ip_mode ? String(live.mgmt_ip_mode) : null,
    })
  }

  const openOnuWebAdmin = async () => {
    if (!onuId) return
    if (!mgmtIp) {
      toast.error("Management IP is not available for this ONU")
      return
    }
    setWebAdminBusy(true)
    try {
      const { mgmt_ip } = await openOnuWebAdminLink(() => smartoltService.getOnuWebAdminLink(onuId))
      toast.success(`Opening ONU Web Admin (${mgmt_ip})`)
    } catch (err: any) {
      toast.error(err?.message || err?.response?.data?.error || "Unable to open ONU Web Admin")
    } finally {
      setWebAdminBusy(false)
    }
  }

  const applyConfigPreset = async () => {
    if (!presetKey) {
      toast.error("Select a configuration preset")
      return
    }
    const preset = configPresets.find((item) => String(item.id || item.name) === presetKey)
    if (!preset) {
      toast.error("Invalid preset")
      return
    }
    setPresetBusy(true)
    try {
      const form_data: Record<string, string> = preset.id
        ? { configuration_preset_id: String(preset.id) }
        : { configuration_preset_name: String(preset.name) }
      await smartoltService.applyConfigurationPreset(onuId, form_data)
      await smartoltService.runCommandsNow(5)
      toast.success("Configuration preset apply command sent")
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to apply preset")
    } finally {
      setPresetBusy(false)
    }
  }

  const removeConfigPreset = async () => {
    const ok = window.confirm("Remove configuration preset from this ONU?")
    if (!ok) return
    setPresetBusy(true)
    try {
      await smartoltService.removeConfigurationPreset(onuId)
      await smartoltService.runCommandsNow(5)
      toast.success("Configuration preset remove command sent")
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Failed to remove preset")
    } finally {
      setPresetBusy(false)
    }
  }

  const runDiagnostic = async (kindOption: DiagnosticKind) => {
    if (!onuId) return
    setDiagnosticKind(kindOption)
    setDiagnosticBusy(true)
    setRouterHosts(null)
    setDiagnosticPayload(null)
    setDiagnosticText("")
    setLiveMonitor(false)
    try {
      const opKey =
        kindOption === "status"
          ? "get_onu_full_status_info_by_external_id"
          : kindOption === "config"
            ? "get_onu_running_config_by_external_id"
            : kindOption === "router_hosts"
              ? "get_onu_router_hosts_by_external_id"
              : kindOption === "speed_profiles"
                ? "get_onu_speed_profiles_by_external_id"
                : "get_onu_details_by_external_id"
      const result = await smartoltService.readOperation(opKey, {
        path_params: { onu_external_id: onuId },
      })
      const payload = result?.response || result?.data || result
      setDiagnosticPayload(payload)
      if (kindOption === "router_hosts") {
        setRouterHosts(extractRouterHosts(payload))
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Read failed")
      setDiagnosticPayload(null)
      setDiagnosticText(err?.response?.data?.error || "Read failed")
    } finally {
      setDiagnosticBusy(false)
    }
  }

  const openModal = (type: ModalType, extra?: Partial<ModalContextData>) => {
    if (!onuId) return
    const primaryWifi = wifiPorts[0]
    const service = servicePorts[0]
    setModalContext({
      onuExternalId: onuId,
      onuName: name,
      currentSn: row?.sn || live.sn,
      currentWifi: {
        port: primaryWifi?.port || "1",
        ssid: primaryWifi?.ssid || "",
        password: primaryWifi?.password || "",
        auth_mode: primaryWifi?.auth_mode || "wpa2-psk",
      },
      currentSpeed: {
        upload_profile: extra?.currentSpeed?.upload_profile || service?.upload_speed,
        download_profile: extra?.currentSpeed?.download_profile || service?.download_speed,
      },
      currentLocation: {
        zone: row?.zone_name || live.zone_name,
        odb: row?.odb_name || live.odb_name,
        odb_port: live.odb_port || live.odb_splitter_port,
        address: row?.address || live.address_or_comment || live.address,
        latitude: row?.latitude ?? readAny(live, "latitude", "gps_latitude", "onu_latitude"),
        longitude: row?.longitude ?? readAny(live, "longitude", "gps_longitude", "onu_longitude"),
      },
      currentWan: { mode: live.wan_mode || live.mode || row?.wan_mode },
      ...extra,
    })
    setActiveModal(type)
  }

  const handleModalSubmit = async (operationKey: string, payload: Record<string, unknown>) => {
    if (!onuId) return false
    setBusy(true)
    try {
      await queueSmartOltOnuCommand(onuId, operationKey, payload)
      toast.success("Command queued")
      setActiveModal(null)
      if (operationKey.includes("delete")) {
        navigate(SMARTOLT_PATHS.configured)
        return true
      }
      setTimeout(() => load(true), 1500)
      return true
    } catch (err: any) {
      toast.error(err?.response?.data?.error || "Action failed")
      return false
    } finally {
      setBusy(false)
    }
  }

  const submitVoip = async () => {
    if (!voipPort) return
    await runAction("enable_onu_voip_port_by_external_id", "Configure VoIP", {
      form_data: {
        voip_port: voipPort.port || voipPort.id || "1",
        ...voipForm,
      },
    })
    setVoipPort(null)
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: "overview", label: "Overview" },
    { id: "stats", label: "Stats" },
    { id: "graphs", label: "Graphs" },
    { id: "configuration", label: "Configuration" },
    { id: "actions", label: "Actions" },
    { id: "alarms", label: "Alarms" },
    { id: "crm", label: "CRM" },
  ]

  return (
    <div className="space-y-6">
      <div className="grid min-w-0 gap-4 lg:grid-cols-[320px_minmax(0,1fr)] lg:gap-6 items-start">
        <aside className="rounded-xl border border-slate-200/80 bg-white p-3 shadow-xs space-y-3 sm:rounded-2xl sm:p-5 sm:space-y-4 lg:sticky lg:top-20">
          <div className="mb-3 flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate(SMARTOLT_PATHS.configured)}
              className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Configured
            </button>
            <div className="flex items-center gap-1.5">
              <button
                type="button"
                title="Refresh live detail from SmartOLT"
                disabled={loading || busy}
                onClick={() => load(true)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-40 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin text-blue-600" : ""}`} />
              </button>
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50 border border-blue-200 text-blue-600">
                <Router className="h-4 w-4" />
              </div>
            </div>
          </div>
          <div className="mb-3 -mx-1 flex items-center gap-1.5 overflow-x-auto px-1 pb-1 sm:mx-0 sm:flex-wrap sm:overflow-visible sm:px-0 sm:pb-0">
            <span className="mr-1 hidden text-[11px] uppercase tracking-wider text-slate-500 font-semibold sm:inline">Actions:</span>
            <button
              type="button"
              title="Reboot"
              disabled={busy}
              onClick={() => runAction("reboot_onu_by_external_id", "Reboot")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100 disabled:opacity-40 transition-colors"
            >
              <Power className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Resync config"
              disabled={busy}
              onClick={() => runAction("resync_onu_config_by_external_id", "Resync")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-purple-200 bg-purple-50 text-purple-700 hover:bg-purple-100 disabled:opacity-40 transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Enable ONU"
              disabled={busy}
              onClick={() => runAction("enable_onu_by_external_id", "Enable")}
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-40 transition-colors"
            >
              <ShieldCheck className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Disable ONU"
              disabled={busy}
              onClick={() =>
                openModal("confirm_destructive", {
                  destructiveAction: {
                    key: "disable_onu_by_external_id",
                    title: "Disable ONU (Administrative Lock)",
                    description: "This will administratively suspend optical transmission on this ONT until re-enabled.",
                    confirmKeyword: "DISABLE",
                    buttonLabel: "Disable Optical Link",
                  },
                })
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-40 transition-colors"
            >
              <Ban className="h-4 w-4" />
            </button>
            <button
              type="button"
              title="Delete ONU"
              disabled={busy}
              onClick={() =>
                openModal("confirm_destructive", {
                  destructiveAction: {
                    key: "delete_onu_by_external_id",
                    title: "De-authorize & Delete ONU",
                    description: "This will delete ONT authorization from the OLT. Network access will stop immediately.",
                    confirmKeyword: "DELETE",
                    buttonLabel: "De-authorize ONU",
                  },
                })
              }
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-rose-300 bg-rose-100 text-rose-800 hover:bg-rose-200 disabled:opacity-40 transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:hidden">
            {[
              ["Status", live.status || row?.status],
              ["ONU RX", formatSignal(rxPower)],
              ["Mgmt IP", mgmtIp],
              ["Type", live.onu_type_name || row?.onu_type_name],
            ].map(([label, value]) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <div className="text-[10px] font-semibold uppercase text-slate-500">{label}</div>
                <div className="mt-0.5 truncate text-xs font-bold text-slate-900" title={dash(value)}>
                  {label === "Status" ? (
                    <span className={`inline-flex items-center gap-1.5 ${kind === "online" ? "text-emerald-700" : kind === "los" ? "text-rose-700" : "text-slate-700"}`}>
                      <span className={`h-2 w-2 rounded-full ${kind === "online" ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {dash(value)}
                    </span>
                  ) : (
                    dash(value)
                  )}
                </div>
              </div>
            ))}
          </div>
          <dl className="hidden space-y-2 text-xs divide-y divide-slate-100 sm:block">
            {identity.map((item, idx) => (
              <div key={item.label} className={`flex justify-between gap-2 ${idx > 0 ? "pt-2" : ""}`}>
                <dt className="text-slate-500 font-medium">
                  {item.href ? (
                    <Link to={item.href} className="text-blue-600 hover:text-blue-800 font-semibold hover:underline">
                      {item.label}
                    </Link>
                  ) : (
                    item.label
                  )}
                </dt>
                <dd className="max-w-[170px] truncate text-right font-medium text-slate-800" title={dash(item.value)}>
                  {item.label === "Status" ? (
                    <span className={`inline-flex items-center gap-1.5 font-bold ${kind === "online" ? "text-emerald-700" : kind === "los" ? "text-rose-700" : "text-slate-600"}`}>
                      <span className={`h-2 w-2 rounded-full ${kind === "online" ? "bg-emerald-500" : "bg-slate-400"}`} />
                      {dash(item.value)}
                    </span>
                  ) : item.action ? (
                    <button type="button" className="text-blue-600 hover:text-blue-800 font-semibold hover:underline" onClick={item.action}>
                      {dash(item.value)}
                    </button>
                  ) : item.href ? (
                    <Link to={item.href} className="text-blue-600 hover:text-blue-800 font-semibold hover:underline">
                      {dash(item.value)}
                    </Link>
                  ) : (
                    dash(item.value)
                  )}
                </dd>
              </div>
            ))}
          </dl>
          <div className="mt-3 hidden flex-wrap gap-1.5 sm:flex">
            {wifiPorts.length > 0 ? <span className="rounded-md border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">WiFi</span> : null}
            {hasCapability(live.voip || row?.voip, voipPorts) ? <span className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">Telephone</span> : null}
            {hasCapability(live.catv || row?.catv_status, []) ? <span className="rounded-md border border-cyan-200 bg-cyan-50 px-2 py-0.5 text-[11px] font-semibold text-cyan-700">CATV</span> : null}
          </div>
        </aside>

        <section className="min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xs">
          <div className="flex gap-1 overflow-x-auto border-b border-slate-200 bg-slate-50/70 p-2 sm:flex-wrap">
            {tabs.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setTab(item.id)}
                className={`shrink-0 rounded-lg px-3.5 py-1.5 text-xs font-semibold transition-all ${
                  tab === item.id
                    ? "bg-white text-blue-700 shadow-xs border border-slate-200"
                    : "text-slate-600 hover:bg-white/60 hover:text-slate-900"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="p-3 sm:p-4">
            {detailWarning && !loading ? (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                Live SmartOLT detail is unavailable. Showing cached inventory data. Upstream error: {detailWarning}
              </div>
            ) : null}
            {loading && <p className="text-sm text-slate-500">Loading ONU details…</p>}

            {tab === "overview" && !loading && (
              <div className="space-y-4">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ["ONU RX", formatSignal(rxPower)],
                    ["OLT RX", formatSignal(oltRxPower)],
                    ["Distance", formatMeters(distance)],
                    ["Mgmt IP", dash(mgmtIp)],
                    ["Type", dash(live.onu_type_name || row?.onu_type_name)],
                    ["Status", dash(live.status || row?.status)],
                    ["VLANs", dash(vlan)],
                    ["WAN mode", dash(wanLabel(wanMode) || wanMode)],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-center shadow-xs">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                      <div className="mt-1 text-lg font-bold text-slate-900">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ["Latency", formatMetric(latency, "ms")],
                    ["Temperature", formatMetric(temp, "C")],
                    ["Voltage", formatMetric(voltage, "V")],
                    ["Bias current", formatMetric(biasCurrent, "mA")],
                    ["Download", dash(downloadRate)],
                    ["Upload", dash(uploadRate)],
                    ["TR-069", dash(tr069)],
                    ["Authorized", formatAuthDate(live.authorized_on || live.auth_date || row?.auth_date)],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 shadow-xs">
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
                      <div className="mt-1 truncate text-sm font-bold text-slate-800" title={dash(value)}>{value}</div>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500 font-semibold">LAN</span>
                  {ethernetPorts.length === 0 ? (
                    <span className="text-slate-400">-</span>
                  ) : (
                    ethernetPorts.map((eth, index) => (
                      <button
                        type="button"
                        onClick={() => openModal("lan_port", { currentWan: { mode: eth.mode || eth.status || "" } })}
                        key={index}
                        className={`rounded-md border px-2 py-1 font-medium transition-colors ${String(eth.status || "").toLowerCase().includes("on") ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "border-slate-200 bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                      >
                        {dash(eth.port || `LAN${index + 1}`)} {dash(eth.status)}
                      </button>
                    ))
                  )}
                  {wifiPorts.length > 0 ? <button type="button" onClick={() => openModal("wifi")} className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700 font-medium hover:bg-blue-100">Wi-Fi {wifiPorts.length}</button> : null}
                  {voipPorts.length > 0 || row?.voip ? <button type="button" onClick={() => setTab("configuration")} className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-700 font-medium hover:bg-indigo-100">VoIP {voipPorts.length || ""}</button> : null}
                  {catvOn ? <button type="button" onClick={toggleCatv} className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-700 font-medium hover:bg-emerald-100">CATV</button> : null}
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-bold text-slate-900">Network & WAN</h3>
                    <div className="flex flex-wrap gap-2">
                      <button type="button" onClick={() => openModal("wan")} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors">
                        <Globe className="h-3.5 w-3.5" />
                        Configure WAN
                      </button>
                      <button
                        type="button"
                        onClick={openOnuWebAdmin}
                        disabled={!mgmtIp || webAdminBusy}
                        className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 disabled:cursor-not-allowed disabled:opacity-50 transition-colors"
                        title={mgmtIp ? "Open ONU Web Admin through proxy" : "Management IP is required"}
                      >
                        {webAdminBusy ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="h-3.5 w-3.5" />}
                        Web Admin
                      </button>
                      <button type="button" onClick={openMgmtIpModal} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                        <Key className="h-3.5 w-3.5" />
                        Mgmt IP
                      </button>
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                    {[
                      ["PPPoE user", pppoeUsername, "pppoe"],
                      ["PPPoE password", pppoePassword ? "********" : null, null],
                      ["Management IP", mgmtIp, "mgmt_ip"],
                      ["Gateway", mgmtGateway, null],
                      ["Subnet", mgmtSubnet, null],
                      ["DNS", mgmtDns1 && mgmtDns2 ? `${mgmtDns1} · ${mgmtDns2}` : mgmtDns1 || mgmtDns2, null],
                      ["WAN mode", wanLabel(wanMode) || wanMode, null],
                    ].map(([label, value, copyKey]) => (
                      <div key={String(label)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 shadow-xs">
                        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-medium">{label}</div>
                        <div className="mt-0.5 flex items-center justify-between gap-2">
                          <span className="truncate font-mono text-xs font-bold text-slate-800">{dash(value)}</span>
                          {copyKey && value ? (
                            <button type="button" onClick={() => handleCopy(String(copyKey), String(value), String(label))} className="shrink-0 text-slate-400 hover:text-blue-600 transition-colors">
                              {copiedKey === copyKey ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                            </button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {tab === "stats" && !loading && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("status")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <Info className="h-3.5 w-3.5 text-blue-600" />
                    Get Status
                  </button>
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("config")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <FileCode className="h-3.5 w-3.5 text-blue-600" />
                    Running Config
                  </button>
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("software")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <Cpu className="h-3.5 w-3.5 text-blue-600" />
                    SW Info
                  </button>
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("router_hosts")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <Router className="h-3.5 w-3.5 text-blue-600" />
                    Router Hosts
                  </button>
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("speed_profiles")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <Zap className="h-3.5 w-3.5 text-amber-500" />
                    Speed Profiles
                  </button>
                  <button type="button" disabled={diagnosticBusy} onClick={() => runDiagnostic("details")} className="inline-flex h-9 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                    <Layers className="h-3.5 w-3.5 text-blue-600" />
                    Full Details
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLiveMonitor((on) => !on)
                      setDiagnosticKind(null)
                      setDiagnosticPayload(null)
                      setDiagnosticText("")
                    }}
                    className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-xs font-semibold transition-all ${liveMonitor ? "border border-rose-300 bg-rose-50 text-rose-700 shadow-xs" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs"}`}
                  >
                    <Signal className="h-3.5 w-3.5" />
                    {liveMonitor ? "Stop Live" : "Live Monitor"}
                  </button>
                </div>
                {diagnosticBusy ? <p className="text-sm text-slate-500">Loading {diagnosticKind === "config" ? "config" : diagnosticKind === "software" ? "SW info" : "status"}…</p> : null}
                {liveMonitor ? (liveSample || liveError ? <LiveSignalViewer payload={liveSample} error={liveError} /> : <p className="text-sm text-slate-500">Reading live signal...</p>) : null}
                {!liveMonitor && diagnosticKind === "router_hosts" && routerHosts ? <RouterHostsTable hosts={routerHosts} /> : null}
                {!liveMonitor && diagnosticKind !== "router_hosts" ? <DiagnosticResultViewer kind={diagnosticKind} payload={diagnosticPayload} live={live} row={row} /> : null}
                {!liveMonitor && diagnosticText ? <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">{diagnosticText}</div> : null}
              </div>
            )}

            {tab === "graphs" && (
              <div className="space-y-3">
                <div className="flex flex-wrap gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 text-xs font-semibold">
                  <button
                    type="button"
                    onClick={() => setGraphSubTab("smartolt_graphs")}
                    className={`rounded-lg px-3 py-1.5 transition-all ${graphSubTab === "smartolt_graphs" ? "bg-white text-blue-700 shadow-xs font-bold" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    Historical graphs
                  </button>
                  <button
                    type="button"
                    onClick={() => setGraphSubTab("live_test")}
                    className={`rounded-lg px-3 py-1.5 transition-all ${graphSubTab === "live_test" ? "bg-white text-emerald-700 shadow-xs font-bold" : "text-slate-600 hover:text-slate-900"}`}
                  >
                    Live traffic test
                  </button>
                </div>
                {graphSubTab === "smartolt_graphs" ? (
                  <>
                    <div className="flex flex-wrap gap-2">
                      {(["signal", "traffic"] as GraphKind[]).map((kindOption) => (
                        <button key={kindOption} type="button" onClick={() => setGraphKind(kindOption)} className={`h-8 rounded-lg px-3 text-xs font-semibold transition-all ${graphKind === kindOption ? "bg-blue-600 text-white shadow-xs" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs"}`}>
                          {kindOption === "signal" ? "Signal" : "Traffic"}
                        </button>
                      ))}
                      {(["hourly", "daily", "weekly", "monthly", "yearly"] as GraphType[]).map((type) => (
                        <button key={type} type="button" onClick={() => setGraphType(type)} className={`h-8 rounded-lg px-3 text-xs font-semibold capitalize transition-all ${graphType === type ? "bg-blue-600 text-white shadow-xs" : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-xs"}`}>
                          {type}
                        </button>
                      ))}
                    </div>
                    {graphLoading ? <p className="text-sm text-slate-500">Loading graph…</p> : null}
                    {graphUrl ? <img src={graphUrl} alt={`${graphKind} ${graphType}`} className="max-w-full rounded-xl border border-slate-200 bg-white shadow-xs object-contain" /> : !graphLoading ? <p className="text-sm text-slate-500">No graph available.</p> : null}
                  </>
                ) : (
                  <LiveTrafficTestConsole
                    onuExternalId={onuId}
                    customerId={row?.customer_id}
                    targetLabel={row?.customer_internet_id || name}
                    compact
                  />
                )}
              </div>
            )}

            {tab === "configuration" && !loading && (
              <div className="space-y-5">
                <div className="rounded-xl border border-slate-200 bg-slate-50/50 p-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                    <h2 className="text-sm font-bold text-slate-900">Configuration preset</h2>
                    <div className="flex flex-wrap gap-2">
                      <select
                        value={presetKey}
                        onChange={(e) => setPresetKey(e.target.value)}
                        className="h-8 min-w-[180px] rounded-lg border border-slate-200 bg-white px-2.5 text-xs text-slate-800 shadow-xs focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Select preset…</option>
                        {configPresets.map((preset) => (
                          <option key={String(preset.id || preset.name)} value={String(preset.id || preset.name)}>
                            {preset.name || preset.id}
                          </option>
                        ))}
                      </select>
                      <button type="button" disabled={presetBusy} onClick={applyConfigPreset} className="h-8 rounded-lg bg-blue-600 px-3.5 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50">
                        Apply
                      </button>
                      <button type="button" disabled={presetBusy} onClick={removeConfigPreset} className="h-8 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">
                        Remove
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500">Apply or remove SmartOLT authorization presets on this ONU.</p>
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <h2 className="text-sm font-bold text-slate-900">Wi-Fi ports</h2>
                    <button type="button" onClick={() => openModal("wifi")} className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors">
                      <Wifi className="h-3.5 w-3.5" />
                      Configure Wi-Fi
                    </button>
                  </div>
                  {wifiPorts.length === 0 && unconfiguredWifiSlots.length === 0 ? (
                    <p className="text-xs text-slate-500">No Wi-Fi ports reported.</p>
                  ) : wifiPorts.length === 0 ? (
                    <p className="text-xs text-slate-500">No active Wi-Fi networks configured on this ONU.</p>
                  ) : (
                    <div className="space-y-2">
                      {wifiPorts.map((wp, index) => {
                        const portKey = String(wp.port || index)
                        const isRadioOn = (wp.admin_state || "").toLowerCase() === "enabled"
                        const isRevealed = revealedWifi[portKey]
                        return (
                          <div key={portKey} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3 text-xs shadow-xs">
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <div>
                                <span className="font-bold text-slate-800">{dash(wp.ssid || wp.port)}</span>
                                <span className="ml-2 text-slate-500">{dash(wp.port)}</span>
                              </div>
                              <div className="flex items-center gap-1.5">
                                <button type="button" onClick={() => toggleWifiRadio(wp.port, wp.admin_state)} className="rounded-md p-1 text-slate-400 hover:bg-slate-200/60 transition-colors">
                                  {isRadioOn ? <WifiOff className="h-3.5 w-3.5 text-rose-600" /> : <Wifi className="h-3.5 w-3.5 text-emerald-600" />}
                                </button>
                                <span className={`rounded-md px-2 py-0.5 text-[10px] font-semibold ${isRadioOn ? "border border-emerald-200 bg-emerald-50 text-emerald-700" : "border border-slate-200 bg-slate-100 text-slate-600"}`}>
                                  {dash(wp.admin_state)}
                                </span>
                              </div>
                            </div>
                            <div className="mt-2 flex items-center justify-between rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-slate-800 shadow-xs">
                              <span className="text-slate-800">{wp.password ? (isRevealed ? wp.password : "••••••••") : "Open / no key"}</span>
                              {wp.password ? (
                                <div className="flex items-center gap-1">
                                  <button type="button" onClick={() => setRevealedWifi((prev) => ({ ...prev, [portKey]: !prev[portKey] }))} className="p-1 text-slate-400 hover:text-slate-700">
                                    {isRevealed ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                                  </button>
                                  <button type="button" onClick={() => handleCopy(`wifi-${portKey}`, String(wp.password), "Wi-Fi password")} className="p-1 text-slate-400 hover:text-blue-600">
                                    {copiedKey === `wifi-${portKey}` ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        )
                      })}
                      {unconfiguredWifiSlots.length > 0 && (
                        <details className="group">
                          <summary className="cursor-pointer list-none text-[11px] font-medium text-slate-400 hover:text-slate-600 transition-colors select-none">
                            <span className="group-open:hidden">
                              + Show {unconfiguredWifiSlots.length} unused guest slot{unconfiguredWifiSlots.length > 1 ? "s" : ""}
                            </span>
                            <span className="hidden group-open:inline">− Hide unused guest slots</span>
                          </summary>
                          <div className="mt-2 space-y-1.5">
                            {unconfiguredWifiSlots.map((wp: any, index: number) => (
                              <div key={String(wp.port || `unused-${index}`)} className="rounded-xl border border-dashed border-slate-200 bg-slate-50/40 px-3 py-2 text-xs text-slate-400">
                                <div className="flex items-center justify-between">
                                  <span className="font-mono">{dash(wp.port)}</span>
                                  <span className="rounded-md border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-400">Unconfigured</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">Speed Profile</h2>
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                        <tr>
                          <th className="px-3 py-2 text-left">Service-port ID</th>
                          <th className="px-3 py-2 text-left">User-VLAN</th>
                          <th className="px-3 py-2 text-left">Download</th>
                          <th className="px-3 py-2 text-left">Upload</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {servicePorts.length === 0 ? (
                          <tr><td colSpan={5} className="py-4 text-center text-slate-500">No service ports</td></tr>
                        ) : (
                          servicePorts.map((service, index) => (
                            <tr key={index} className="transition-colors hover:bg-slate-50/80">
                              <td className="px-3 py-2 font-mono font-medium text-slate-800">{dash(service.service_port || service.id)}</td>
                              <td className="px-3 py-2 font-mono text-slate-600">{dash(service.vlan || service.cvlan)}</td>
                              <td className="px-3 py-2 font-semibold text-slate-800">{dash(service.download_speed)}</td>
                              <td className="px-3 py-2 font-semibold text-slate-800">{dash(service.upload_speed)}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() =>
                                    openModal("speed", {
                                      currentSpeed: {
                                        upload_profile: service.upload_speed,
                                        download_profile: service.download_speed,
                                      },
                                    })
                                  }
                                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
                                >
                                  Configure
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">Ethernet Ports</h2>
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                        <tr>
                          <th className="px-3 py-2 text-left">Port</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Admin State</th>
                          <th className="px-3 py-2 text-left">Mode</th>
                          <th className="px-3 py-2 text-left">DHCP</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {ethernetPorts.length === 0 ? (
                          <tr><td colSpan={6} className="py-4 text-center text-slate-500">No ethernet ports</td></tr>
                        ) : (
                          ethernetPorts.map((eth, index) => (
                            <tr key={index} className="transition-colors hover:bg-slate-50/80">
                              <td className="px-3 py-2 font-mono font-bold text-slate-800">{dash(eth.port)}</td>
                              <td className="px-3 py-2">{dash(eth.status)}</td>
                              <td className="px-3 py-2">{dash(eth.admin_state)}</td>
                              <td className="px-3 py-2">{dash(eth.mode)}</td>
                              <td className="px-3 py-2">{dash(eth.dhcp)}</td>
                              <td className="px-3 py-2 text-right">
                                <button type="button" onClick={() => openModal("lan_port")} className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                                  Configure
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div>
                  <h2 className="mb-2 text-sm font-bold text-slate-900">VoIP POTS Ports</h2>
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                        <tr>
                          <th className="px-3 py-2 text-left">Port</th>
                          <th className="px-3 py-2 text-left">Profile</th>
                          <th className="px-3 py-2 text-left">Number</th>
                          <th className="px-3 py-2 text-right">Action</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 text-slate-700">
                        {voipPorts.length === 0 ? (
                          <tr><td colSpan={4} className="py-4 text-center text-slate-500">No VoIP ports</td></tr>
                        ) : (
                          voipPorts.map((pots, index) => (
                            <tr key={index} className="transition-colors hover:bg-slate-50/80">
                              <td className="px-3 py-2 font-mono font-bold text-slate-800">{dash(pots.port)}</td>
                              <td className="px-3 py-2">{dash(pots.profile || pots.voip_profile)}</td>
                              <td className="px-3 py-2 font-mono text-slate-700">{dash(pots.telephone_number || pots.number)}</td>
                              <td className="px-3 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setVoipForm({
                                      voip_profile_name: pots.profile || pots.voip_profile || "",
                                      phone_number: pots.telephone_number || pots.number || "",
                                      sip_userid: pots.username || pots.sip_userid || "",
                                      password: pots.password || "",
                                    })
                                    setVoipPort(pots)
                                  }}
                                  className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors"
                                >
                                  Configure
                                </button>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <div className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 shadow-xs">
                  <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                    <Tv className="h-4 w-4 text-blue-600" />
                    CATV operational state
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={toggleCatv}
                    className={`h-8 rounded-lg px-3 text-xs font-semibold transition-all ${catvOn ? "border border-emerald-200 bg-emerald-50 text-emerald-700 shadow-xs" : "border border-slate-200 bg-white text-slate-600 shadow-xs"}`}
                  >
                    {catvOn ? "Active" : "Off"}
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button type="button" disabled={busy} onClick={() => runAction("enable_onu_by_external_id", "Enable")} className="h-9 rounded-lg bg-emerald-600 px-3.5 text-xs font-semibold text-white shadow-xs hover:bg-emerald-700 disabled:opacity-50">Enable ONU</button>
                  <button type="button" disabled={busy} onClick={() => openModal("wan")} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">WAN</button>
                  <button type="button" disabled={busy} onClick={openMgmtIpModal} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">Mgmt IP</button>
                  <button type="button" disabled={busy} onClick={() => openModal("location")} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">Location</button>
                  <button type="button" disabled={busy} onClick={() => openModal("ont_swap")} className="h-9 rounded-lg border border-purple-200 bg-purple-50 text-purple-700 px-3 text-xs font-semibold hover:bg-purple-100 transition-colors">ONT swap</button>
                  <button type="button" disabled={busy} onClick={() => openModal("web_password")} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">Web password</button>
                </div>
              </div>
            )}

            {tab === "actions" && !loading && (
              <div className="space-y-3">
                <div className="flex gap-1 overflow-x-auto rounded-xl border border-slate-200 bg-slate-100 p-1 text-xs select-none sm:grid sm:grid-cols-5 sm:overflow-visible">
                  {(
                    [
                      ["quick", "Quick", Zap],
                      ["wan", "WAN", Globe],
                      ["lan", "Ports", Cable],
                      ["security", "Security", Lock],
                      ["services", "Services", Layers],
                    ] as const
                  ).map(([id, label, Icon]) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => setActionCategory(id)}
                      className={`flex min-w-[92px] items-center justify-center gap-1.5 rounded-lg px-2 py-2 font-bold transition-all sm:min-w-0 ${actionCategory === id ? "bg-white text-blue-700 shadow-xs" : "text-slate-600 hover:text-slate-900"}`}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {label}
                    </button>
                  ))}
                </div>

                {actionCategory === "quick" && (
                  <div className="grid gap-2 sm:grid-cols-2">
                    <button type="button" disabled={busy} onClick={() => runAction("reboot_onu_by_external_id", "Reboot")} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Reboot ONU</button>
                    <button type="button" disabled={busy} onClick={() => runAction("resync_onu_config_by_external_id", "Resync")} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Resync config</button>
                    <button type="button" disabled={busy} onClick={() => runAction("enable_onu_by_external_id", "Enable")} className="h-9 rounded-lg border border-emerald-200 bg-emerald-50 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 transition-colors disabled:opacity-50">Enable optical link</button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        openModal("confirm_destructive", {
                          destructiveAction: {
                            key: "disable_onu_by_external_id",
                            title: "Disable ONU",
                            description: "Administratively suspend optical transmission on this ONT.",
                            confirmKeyword: "DISABLE",
                            buttonLabel: "Disable",
                          },
                        })
                      }
                      className="h-9 rounded-lg border border-rose-200 bg-rose-50 text-xs font-semibold text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-50"
                    >
                      Disable optical link
                    </button>
                  </div>
                )}

                {actionCategory === "wan" && (
                  <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => openModal("wan")} className="h-9 w-full rounded-lg bg-cyan-600 text-xs font-semibold text-white shadow-xs hover:bg-cyan-700 disabled:opacity-50">Configure WAN (PPPoE / Static / DHCP)</button>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button type="button" disabled={busy} onClick={() => handleModalSubmit("enable_onu_allow_remote_access_wan_ip_by_external_id", {})} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Allow remote WAN</button>
                      <button type="button" disabled={busy} onClick={() => handleModalSubmit("disable_onu_allow_remote_access_wan_ip_by_external_id", {})} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Block remote WAN</button>
                    </div>
                    <button type="button" disabled={busy} onClick={openMgmtIpModal} className="h-9 w-full rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Configure management IP</button>
                  </div>
                )}

                {actionCategory === "lan" && (
                  <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => openModal("lan_port")} className="h-9 w-full rounded-lg bg-indigo-600 text-xs font-semibold text-white shadow-xs hover:bg-indigo-700 disabled:opacity-50">Configure Ethernet ports & VLANs</button>
                    <button type="button" disabled={busy} onClick={() => openModal("wifi")} className="h-9 w-full rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">Configure Wi-Fi</button>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button type="button" disabled={busy} onClick={() => handleModalSubmit("enable_onu_ip_dhcp_snooping_by_external_id", {})} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">DHCP snooping on</button>
                      <button type="button" disabled={busy} onClick={() => handleModalSubmit("disable_onu_ip_dhcp_snooping_by_external_id", {})} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors disabled:opacity-50">DHCP snooping off</button>
                    </div>
                  </div>
                )}

                {actionCategory === "security" && (
                  <div className="space-y-2">
                    <button type="button" disabled={busy} onClick={() => openModal("web_password")} className="h-9 w-full rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50 transition-colors">Change ONT web admin password</button>
                    <button type="button" disabled={busy} onClick={() => openModal("ont_swap")} className="h-9 w-full rounded-lg border border-purple-200 bg-purple-50 text-xs font-semibold text-purple-700 hover:bg-purple-100 transition-colors disabled:opacity-50">ONT hardware swap (SN change)</button>
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          openModal("confirm_destructive", {
                            destructiveAction: {
                              key: "restore_onu_factory_defaults_by_external_id",
                              title: "Factory reset ONT",
                              description: "Wipes local ONT configuration including Wi-Fi and static IP settings.",
                              confirmKeyword: "RESTORE",
                              buttonLabel: "Factory reset",
                            },
                          })
                        }
                        className="h-9 rounded-lg border border-amber-200 bg-amber-50 text-xs font-semibold text-amber-700 hover:bg-amber-100 transition-colors disabled:opacity-50"
                      >
                        Factory reset
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() =>
                          openModal("confirm_destructive", {
                            destructiveAction: {
                              key: "delete_onu_by_external_id",
                              title: "Delete ONU",
                              description: "Removes ONT authorization from the OLT.",
                              confirmKeyword: "DELETE",
                              buttonLabel: "Delete ONU",
                            },
                          })
                        }
                        className="h-9 rounded-lg border border-rose-200 bg-rose-50 text-xs font-semibold text-rose-700 hover:bg-rose-100 transition-colors disabled:opacity-50"
                      >
                        Delete ONU
                      </button>
                    </div>
                  </div>
                )}

                {actionCategory === "services" && (
                  <div className="space-y-2">
                    <div className="grid gap-2 sm:grid-cols-2">
                      <button type="button" disabled={busy} onClick={toggleCatv} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50 transition-colors">Toggle CATV</button>
                      <button type="button" disabled={busy} onClick={() => handleModalSubmit("enable_onu_iptv_by_external_id", {})} className="h-9 rounded-lg border border-slate-200 bg-white text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50 transition-colors">Enable IPTV</button>
                    </div>
                    <button type="button" disabled={busy} onClick={() => openModal("speed")} className="h-9 w-full rounded-lg bg-blue-600 text-xs font-semibold text-white shadow-xs hover:bg-blue-700 disabled:opacity-50">Modify bandwidth profiles</button>
                    <button type="button" disabled={busy} onClick={() => openModal("location")} className="h-9 w-full rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-700 hover:bg-slate-50 shadow-xs disabled:opacity-50 transition-colors">
                      <MapPin className="mr-1 inline h-3.5 w-3.5 text-rose-600" />
                      Update location / zone / ODB
                    </button>
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-white shadow-xs">
                  <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-2.5 text-xs font-bold text-slate-800">Recent commands</div>
                  <div className="max-h-48 overflow-auto divide-y divide-slate-100">
                    {commands.length === 0 ? (
                      <p className="px-4 py-4 text-xs text-slate-500">No commands yet. Open Alarms → Command history for full log.</p>
                    ) : (
                      commands.slice(0, 8).map((command) => (
                        <div key={command.id} className="px-4 py-2.5 text-xs">
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-semibold text-slate-800">{command.display_name || command.action_key}</span>
                            <span className="text-slate-500 font-mono text-[11px]">{command.status}</span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === "alarms" && (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 text-xs font-semibold">
                    <button type="button" onClick={() => setAlarmsSubTab("alarms")} className={`rounded-lg px-3 py-1.5 transition-all ${alarmsSubTab === "alarms" ? "bg-white text-blue-700 shadow-xs font-bold" : "text-slate-600 hover:text-slate-900"}`}>
                      SmartOLT alarms
                    </button>
                    <button type="button" onClick={() => setAlarmsSubTab("commands")} className={`rounded-lg px-3 py-1.5 transition-all ${alarmsSubTab === "commands" ? "bg-white text-blue-700 shadow-xs font-bold" : "text-slate-600 hover:text-slate-900"}`}>
                      Command history
                    </button>
                  </div>
                  <Link to={`${SMARTOLT_PATHS.alarms}?q=${encodeURIComponent(onuId)}`} className="inline-flex h-8 items-center rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">
                    All alarms
                  </Link>
                </div>
                {alarmsSubTab === "alarms" ? (
                  alarmsLoading ? (
                    <p className="text-sm text-slate-500">Loading alarms…</p>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                      <table className="w-full text-xs">
                        <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                          <tr>
                            <th className="px-3 py-2 text-left">Date</th>
                            <th className="px-3 py-2 text-left">Severity</th>
                            <th className="px-3 py-2 text-left">Message</th>
                            <th className="px-3 py-2 text-left">Content</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                          {alarms.length === 0 ? (
                            <tr><td colSpan={4} className="py-6 text-center text-slate-500">No SmartOLT alarms for this ONU.</td></tr>
                          ) : (
                            alarms.slice(0, 40).map((alarm) => (
                              <tr key={alarm.id} className="transition-colors hover:bg-slate-50/80">
                                <td className="px-3 py-2 font-mono text-slate-500">{dateTime(alarm.time)}</td>
                                <td className="px-3 py-2 font-semibold">{alarm.severity}</td>
                                <td className="px-3 py-2 text-slate-800 font-medium">{alarm.message}</td>
                                <td className="px-3 py-2 text-slate-500 font-mono text-[11px]">{alarm.content || alarm.raw || "—"}</td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-xs">
                    <table className="w-full text-xs">
                      <thead className="border-b border-slate-200 bg-slate-50/80 text-[11px] uppercase tracking-wide text-slate-600 font-semibold">
                        <tr>
                          <th className="px-3 py-2 text-left">Date</th>
                          <th className="px-3 py-2 text-left">Action</th>
                          <th className="px-3 py-2 text-left">Status</th>
                          <th className="px-3 py-2 text-left">Detail</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
                        {commands.length === 0 ? (
                          <tr><td colSpan={4} className="py-6 text-center text-slate-500">No recent commands for this ONU.</td></tr>
                        ) : (
                          commands.map((command) => (
                            <tr key={command.id} className="transition-colors hover:bg-slate-50/80">
                              <td className="px-3 py-2 font-mono text-slate-500">{dateTime(command.created_at)}</td>
                              <td className="px-3 py-2 font-semibold text-slate-800">{command.display_name || command.action_key}</td>
                              <td className="px-3 py-2 font-mono text-slate-500">{command.status}</td>
                              <td className="px-3 py-2 text-slate-500">{command.error_message || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {tab === "crm" && !loading && (
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700 shadow-xs">
                {row?.customer_internet_id ? (
                  <p>
                    Linked customer <span className="font-mono font-bold text-blue-700">{row.customer_internet_id}</span>
                    {row.customer_name ? ` · ${row.customer_name}` : ""}
                    {row.customer_id ? (
                      <>
                        {" "}
                        <Link to={`/customers/${row.customer_id}`} className="ml-2 inline-flex items-center rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition-colors">Open CRM</Link>
                      </>
                    ) : null}
                  </p>
                ) : (
                  <p className="text-slate-500">Not linked to a CRM customer.</p>
                )}
              </div>
            )}
          </div>
        </section>
      </div>

      <SmartOLTActionModals
        activeModal={activeModal}
        onClose={() => setActiveModal(null)}
        onSubmit={handleModalSubmit}
        context={modalContext}
        topology={topology}
        busy={busy}
      />

      <SmartOLTMgmtIpModal
        open={Boolean(mgmtModalOnu)}
        onu={mgmtModalOnu}
        onClose={() => setMgmtModalOnu(null)}
        onSuccess={() => {
          setMgmtModalOnu(null)
          load(true)
        }}
      />

      {voipPort ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 backdrop-blur-xs p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl">
            <h3 className="text-sm font-bold text-slate-900">Configure VoIP port {dash(voipPort.port)}</h3>
            <div className="mt-3 space-y-2.5">
              {(["voip_profile_name", "phone_number", "sip_userid", "password"] as const).map((field) => (
                <input
                  key={field}
                  value={voipForm[field]}
                  onChange={(e) => setVoipForm((prev) => ({ ...prev, [field]: e.target.value }))}
                  placeholder={field.replace(/_/g, " ")}
                  className="h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm text-slate-900 placeholder-slate-400 focus:border-blue-500 focus:outline-none shadow-xs"
                />
              ))}
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setVoipPort(null)} className="h-9 rounded-lg border border-slate-200 bg-white px-3 text-sm font-medium text-slate-700 hover:bg-slate-50 shadow-xs transition-colors">Cancel</button>
              <button type="button" onClick={submitVoip} className="h-9 rounded-lg bg-blue-600 px-4 text-sm font-semibold text-white shadow-xs hover:bg-blue-700 transition-colors">Save</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

function hasCapability(value: unknown, list: any[]) {
  if (list.length > 0) return true
  if (value == null || value === false) return false
  const text = String(value).trim().toLowerCase()
  return text !== "" && text !== "-" && text !== "0" && text !== "none" && text !== "disabled" && text !== "off"
}
